import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { SITE } from "../content";

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy — Ridgehawk Logistics"
        description="How Ridgehawk Logistics collects, uses, and protects your personal information when you use ridgehawk.ca."
        path="/privacy"
      />
      <section className="section-pad bg-white" data-testid="privacy-page">
        <div className="container-pad">
          <div className="mx-auto max-w-3xl">
            <span className="overline">Legal</span>
            <h1 className="h1 mt-4">Privacy Policy</h1>
            <p className="body-base mt-4 text-ridge-muted">
              Last updated: April 29, 2026
            </p>

            <div
              className="mt-8 rounded-md border border-ridge-amber/40 bg-ridge-amber/10 p-5"
              data-testid="legal-disclaimer"
            >
              <p className="text-sm leading-relaxed text-ridge-text">
                <strong className="font-semibold">Disclaimer.</strong> This is a starting
                template provided for general informational purposes only. It is not
                legal advice and may not address every requirement that applies to your
                business or jurisdiction. Please consult a qualified lawyer to review
                and tailor this policy before relying on it.
              </p>
            </div>

            <div className="prose-ridge mt-12 max-w-none">
              <h2>1. Who we are</h2>
              <p>
                Ridgehawk Logistics Inc. ("Ridgehawk", "we", "our", or "us") is a
                courier and logistics company headquartered in {SITE.city}, Canada.
                This Privacy Policy explains how we collect, use, disclose, and protect
                personal information in connection with our website at {SITE.domain}{" "}
                and our services.
              </p>

              <h2>2. Information we collect</h2>
              <p>We collect information that you provide directly to us, including:</p>
              <ul>
                <li>
                  <strong>Quote and contact requests:</strong> name, email address,
                  phone number, company, pickup and drop-off cities, service type,
                  cargo details, and any message you submit.
                </li>
                <li>
                  <strong>Chat assistant:</strong> messages you send through the
                  on-site chat assistant, along with a session identifier we use to
                  maintain the conversation.
                </li>
                <li>
                  <strong>Account information:</strong> if you are an authorised
                  Ridgehawk team member, your email and password hash for the admin
                  area.
                </li>
              </ul>
              <p>
                We also automatically collect technical information when you visit the
                site, such as your browser user agent, referring page, and a one-way
                hash of your IP address used for rate limiting and abuse prevention.
              </p>

              <h2>3. How we use your information</h2>
              <ul>
                <li>To respond to quote requests and inquiries.</li>
                <li>To provide, maintain, and improve our courier and logistics services.</li>
                <li>To send transactional emails confirming submissions.</li>
                <li>To prevent fraud, spam, and abuse of the website and chat.</li>
                <li>To comply with applicable legal obligations in Canada.</li>
              </ul>

              <h2>4. Service providers</h2>
              <p>
                We share information with a limited set of trusted third-party service
                providers that help us operate the site and deliver our services.
                These currently include:
              </p>
              <ul>
                <li>
                  <strong>MongoDB Atlas</strong> (or equivalent) — hosted database for
                  storing leads, content, and chat sessions.
                </li>
                <li>
                  <strong>Resend</strong> — transactional email delivery for quote and
                  contact confirmations.
                </li>
                <li>
                  <strong>Cloudflare Turnstile</strong> — anti-spam verification on
                  forms.
                </li>
                <li>
                  <strong>Anthropic / Emergent</strong> — large language model
                  inference for the chat assistant.
                </li>
              </ul>

              <h2>5. Cookies and similar technologies</h2>
              <p>
                We use a minimal number of browser storage entries (localStorage) to
                preserve your chat session and admin login. UTM parameters in inbound
                links are stored briefly to help us understand which marketing
                channels work.
              </p>
              <p>
                We also use <strong>Google Analytics 4 (GA4)</strong> to measure
                aggregated, anonymised page views and engagement so we can improve
                the site. GA4 only loads when you accept analytics cookies on the
                consent banner shown to first-time visitors. Your IP address is
                anonymised at collection (<code>anonymize_ip: true</code>).
              </p>
              <p>
                You can change your choice at any time using the{" "}
                <strong>"Manage cookies"</strong> link in the site footer. Declining
                analytics means no GA4 script is loaded and no analytics cookies are
                set in your browser. The site works fully without analytics enabled.
              </p>
              <p>
                We do not currently use third-party advertising or marketing cookies.
              </p>

              <h2>6. Data retention</h2>
              <p>
                We retain leads, chat transcripts, and related metadata for as long as
                we have an active business relationship with you, and afterwards for
                the period required to comply with our legal obligations, resolve
                disputes, and enforce our agreements.
              </p>

              <h2>7. Your rights</h2>
              <p>
                Subject to applicable Canadian privacy law, you may request access to
                the personal information we hold about you, request that we correct or
                delete it, or withdraw consent to certain processing. To make a
                request, email us at{" "}
                <a href={`mailto:${SITE.email}`}>{SITE.email}</a>.
              </p>

              <h2>8. Security</h2>
              <p>
                We use reasonable technical and organisational safeguards including
                TLS in transit, hashed passwords for admin accounts, hashed IPs, rate
                limiting, and access controls. No system is perfectly secure, however,
                and we cannot guarantee absolute security.
              </p>

              <h2>9. Children</h2>
              <p>
                Our services are intended for businesses and adults. We do not
                knowingly collect personal information from children under the age of
                13.
              </p>

              <h2>10. Changes to this policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The date at the
                top of this page reflects the latest revision. Material changes will
                be communicated through the site.
              </p>

              <h2>11. Contact us</h2>
              <p>
                Questions about this policy or our privacy practices can be sent to{" "}
                <a href={`mailto:${SITE.email}`}>{SITE.email}</a> or through our{" "}
                <Link to="/contact">contact page</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
